import { useExpandedId } from "../../../../common/hooks/useExpandedId";
import { AssociatedAssetDetailData } from "./SystemRelationStep.types";
import { deleteHttp, post, put } from "../../../../common/api/apiShared";
import { useState } from "react";
import { useOptimisticUpdate } from "../../../../common/hooks/useOptimisticUpdate";
import { SystemModel } from "../../../../system/systemEdit/SystemEdit.types";
import { ISystemRelationStep } from "./SystemRelationStep";
import { useDotLegalMutation, useDotLegalPostGetQueryArray, useDotLegalQueryClient, useDotLegalQueryWithKey } from "@common/hooks/dotLegalReactQuery";
import { getSystemEditQueryKey } from "../../../../system/systemEdit/SystemEdit.hooks";
import { IdNamePair } from "../../../../customLists/customListView/CustomListView.types";

export default function useSystemRelationStepHooks(props: ISystemRelationStep) {
    const optimisticUpdate = useOptimisticUpdate();
    const queryClient = useDotLegalQueryClient();
    const { getExpandedId, setExpandedId } = useExpandedId();
    const selectableRelationsQuery = useDotLegalQueryWithKey<Array<IdNamePair>>("selectableSystemRelations", "/system/selectableRelations");
    const [isAddingNewRelation, setIsAddingNewRelation] = useState(false);
    let queryKey = "systemRelation" + props.id;

    const detailsQuery = useDotLegalPostGetQueryArray<Array<AssociatedAssetDetailData>>(
        ["associatedAssetDetails", ...props.relatedSystems],
        "/system/associated-asset-details",
        props.relatedSystems,
        { enabled: props.relatedSystems.length > 0 }
    );

    const systemQueryKey = getSystemEditQueryKey(props.id);
    const addRelationMutation = useDotLegalMutation(addRelationApi, { onSuccess: () => queryClient.refetchQueries(systemQueryKey) });
    const removeSystemMutation = useDotLegalMutation(removeRelationApi, { onSuccess: () => queryClient.refetchQueries(systemQueryKey) });
    const updateRelationMutation = useDotLegalMutation(updateRelationApi);

    const handleRelationChange = async (id: string) => {
        if (isAddingNewRelation) {
            let temp = [...props.relatedSystems];
            temp.push(id);

            await optimisticUpdate.setQueryData(queryKey, temp);
            addRelationMutation.mutate(id);
            setIsAddingNewRelation(false);
        } else {
            let fromSystemId = getExpandedId();

            let temp = [...props.relatedSystems];
            var systemIndex = temp.findIndex((x) => x === fromSystemId);
            temp[systemIndex] = id;

            await optimisticUpdate.setQueryData(queryKey, temp);
            updateRelationMutation.mutate({ fromSystemId: fromSystemId, toSystemId: id });
        }

        setExpandedId(id);
    };

    const handleRemoveSystem = async (systemId: string) => {
        if (getExpandedId() === systemId) setExpandedId("");

        let temp = [...props.relatedSystems];
        temp = temp.filter((x) => x !== systemId);

        await optimisticUpdate.setQueryData(queryKey, temp);

        removeSystemMutation.mutate(systemId);
    };

    function onExpandClick(id: string) {
        if (getExpandedId() === id) {
            setExpandedId("");
        } else {
            setExpandedId(id);
        }
    }

    function getDetailForSystem(systemId: string): AssociatedAssetDetailData | undefined {
        return detailsQuery.data?.find((x) => x.id === systemId);
    }

    return {
        getExpandedId,
        setExpandedId,
        selectableItems: selectableRelationsQuery.data?.filter((x) => x.id !== props.id),
        isLoading: selectableRelationsQuery.isLoading,
        isDetailsLoading: detailsQuery.isLoading,
        handleRelationChange,
        isAddingNewRelation,
        setIsAddingNewRelation,
        onExpandClick,
        handleRemoveSystem,
        getDetailForSystem,
    };

    function convertToSaveModel(systemId2: string) {
        return {
            systemId1: props.id,
            systemId2: systemId2,
        };
    }

    async function addRelationApi(systemId: string) {
        return await post<SystemModel>("/systemRelation/", convertToSaveModel(systemId));
    }

    async function removeRelationApi(systemId: string) {
        return await deleteHttp(`/systemRelation/${props.id}/${systemId}`);
    }

    async function updateRelationApi(data: { fromSystemId: string; toSystemId: string }) {
        return await put(`/systemRelation/${props.id}/`, { fromSystemId: data.fromSystemId, toSystemId: data.toSystemId });
    }
}
